import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, SIZES, fmt } from "@/lib/api";
import { ArrowLeft, Pencil } from "lucide-react";

export default function ReturnView() {
  const { id } = useParams();
  const nav = useNavigate();
  const [ret, setRet] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/returns/${id}`)
      .then(({ data }) => setRet(data))
      .catch(() => setErr("Return not found."));
  }, [id]);

  if (err) {
    return (
      <div data-testid="return-view-page">
        <div className="error-msg" style={{ marginBottom: 14 }}>{err}</div>
        <button className="btn btn-secondary" onClick={() => nav("/returns")}><ArrowLeft size={14}/> Back to Returns</button>
      </div>
    );
  }

  if (!ret) return <div style={{ padding: 20 }}>Loading...</div>;

  return (
    <div data-testid="return-view-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">{ret.return_number}</h1>
          <p className="page-sub">Return dated {ret.return_date}</p>
        </div>
        <div className="toolbar" style={{ gap: 8 }}>
          <button className="btn btn-secondary" data-testid="edit-return-btn" onClick={() => nav(`/returns/${id}/edit`)}><Pencil size={14}/> Edit Return</button>
          <Link to="/returns" className="btn btn-secondary"><ArrowLeft size={14}/> Back to Returns</Link>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div className="grid-form">
          <div>
            <label style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>Customer</label>
            <div style={{ fontWeight: 600, marginTop: 4 }}>{ret.customer_snapshot?.name}</div>
            {ret.customer_snapshot?.phone && <div style={{ color: "var(--text-muted)", fontSize: 13 }}>{ret.customer_snapshot.phone}</div>}
          </div>
          {ret.invoice_id && (
            <div>
              <label style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>Linked Invoice</label>
              <div style={{ marginTop: 4 }}>
                <Link to={`/invoices/${ret.invoice_id}`} style={{ color: "var(--accent)", textDecoration: "none", fontWeight: 600 }}>View original invoice</Link>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="card-flush" style={{ marginBottom: 16 }}>
        <div style={{ padding: "14px 20px", borderBottom: "1px solid var(--border)" }}>
          <h2 style={{ fontSize: 16, fontWeight: 600 }}>Returned Items</h2>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>Style</th>
              <th>Color</th>
              {SIZES.map((s) => <th key={s} className="num">{s}</th>)}
              <th className="num">Qty</th>
              <th className="num">Est. Price</th>
              <th className="num">Est. Amount</th>
            </tr>
          </thead>
          <tbody>
            {(ret.items || []).map((it, i) => {
              const qty = it.qty ?? SIZES.reduce((a, s) => a + (Number(it.sizes?.[s]) || 0), 0);
              const amount = it.line_total ?? (qty * Number(it.unit_price || 0));
              return (
                <tr key={i} data-testid={`ret-view-line-${i}`}>
                  <td><strong>{it.style_id}</strong></td>
                  <td>{it.color}</td>
                  {SIZES.map((s) => <td key={s} className="num">{it.sizes?.[s] || 0}</td>)}
                  <td className="num"><strong>{qty}</strong></td>
                  <td className="num">₹ {fmt(it.unit_price || 0)}</td>
                  <td className="num"><strong>₹ {fmt(amount)}</strong></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="card">
        <h2 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>Refund Details</h2>
        <div className="grid-form">
          <div>
            <label style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>Amount Returned</label>
            <div className="tabular" style={{ fontWeight: 600, marginTop: 4, fontSize: 16 }}>₹ {fmt(ret.amount_returned || 0)}</div>
          </div>
          <div>
            <label style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>Refund Method</label>
            <div style={{ marginTop: 4 }}>{ret.refund_method ? ret.refund_method.toUpperCase() : "— None (exchange / no refund) —"}</div>
          </div>
        </div>
        {ret.note && (
          <div style={{ marginTop: 14 }}>
            <label style={{ fontSize: 12, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: 0.4 }}>Note</label>
            <div style={{ marginTop: 4, color: "var(--text)" }}>{ret.note}</div>
          </div>
        )}
      </div>
    </div>
  );
}
